// Email validation utilities for student signup

const DISPOSABLE_DOMAINS = [
  "mailinator.com", "guerrillamail.com", "tempmail.com", "throwaway.email",
  "yopmail.com", "sharklasers.com", "guerrillamailblock.com", "grr.la",
  "dispostable.com", "trashmail.com", "fakeinbox.com", "mailnesia.com",
  "maildrop.cc", "10minutemail.com", "temp-mail.org", "getnada.com",
  "mohmal.com", "burnermail.io", "tempr.email", "discard.email",
  "tmpmail.net", "tmpmail.org", "bupmail.com", "mailcatch.com",
];

const COMMON_TYPO_DOMAINS: Record<string, string> = {
  "gmial.com": "gmail.com", "gmal.com": "gmail.com", "gmai.com": "gmail.com",
  "gamil.com": "gmail.com", "gnail.com": "gmail.com", "gmail.co": "gmail.com",
  "yahooo.com": "yahoo.com", "yaho.com": "yahoo.com", "yhoo.com": "yahoo.com",
  "hotmal.com": "hotmail.com", "hotmial.com": "hotmail.com", "hotmai.com": "hotmail.com",
  "outlok.com": "outlook.com", "outloo.com": "outlook.com",
};

export interface EmailValidationResult {
  valid: boolean;
  error?: string;
  suggestion?: string;
}

export function validateEmail(email: string): EmailValidationResult {
  const trimmed = email.trim().toLowerCase();

  if (!trimmed) {
    return { valid: false, error: "Email address is required" };
  }

  // Basic format check
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: "Please enter a valid email address format (e.g., name@example.com)" };
  }

  const domain = trimmed.split("@")[1];
  const localPart = trimmed.split("@")[0];

  // Check local part length
  if (localPart.length < 2) {
    return { valid: false, error: "Email address appears too short to be valid" };
  }

  // Check for disposable/temporary email domains
  if (DISPOSABLE_DOMAINS.includes(domain)) {
    return { valid: false, error: "Temporary or disposable email addresses are not allowed. Please use a permanent email." };
  }

  // Check for common typos and suggest correction
  if (COMMON_TYPO_DOMAINS[domain]) {
    return {
      valid: false,
      error: `Did you mean ${localPart}@${COMMON_TYPO_DOMAINS[domain]}? The domain "${domain}" appears to be a typo.`,
      suggestion: `${localPart}@${COMMON_TYPO_DOMAINS[domain]}`,
    };
  }

  // Check for suspicious patterns
  if (/^(test|fake|dummy|asdf|qwerty|abc|xxx|zzz|noemail|no-email|none)@/.test(trimmed)) {
    return { valid: false, error: "This email appears to be fake or placeholder. Please enter your real email address." };
  }

  // Check domain has at least one dot
  if (!domain.includes(".")) {
    return { valid: false, error: "Email domain appears invalid" };
  }

  // Check TLD length
  const tld = domain.split(".").pop() || "";
  if (tld.length < 2 || tld.length > 10) {
    return { valid: false, error: "Email domain extension appears invalid" };
  }

  return { valid: true };
}
